/* eslint-disable max-len */
import * as React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import WarningIcon from "@mui/icons-material/Warning";
import { useNavigate, useSearchParams } from "react-router-dom";
import AlgorithmSelector from "./AlgorithmSelector";
import postSample from "../../../apis/postSample";
import postProject from "../../../apis/postProject.js";
import { parseVCFForReference } from "../../../utils/fileUploader/utils";
const FileUploader = React.lazy(() => import("./FileUploader.jsx"));
const TrioUploader = React.lazy(() => import("./TrioUploader.jsx"));
import HPOSelector from "./HPOSelector";

// Predifined values for algorithms
const mappers = ["BWA2", "BWA", "Bowtie2"];
const variantCallers = [
  "Mutect",
  "Varscan2",
  "Strelka",
  "MuSe",
  "Octopus",
  "SomaticSniper",
  "VarDict",
  "HapotypeCaller",
];
const variantAnnotators = [
  "VEP",
  "Annovar",
  "PharmGKB",
  "InterVAR",
  "CancerVAR",
];

// Predifened values for algorithm keys
const algorithmKeys = ["aligner", "variantCaller", "variantAnnotator"];

function CreateProject(props) {
  // File uploaders
  const [tumorFileUploader, setTumorFileUploader] = React.useState(null);
  const [normalFileUploader, setNormalFileUploader] = React.useState(null);
  const [bedFileUploader, setBedFileUploader] = React.useState(null);
  const [fatherFileUploader, setFatherFileUploader] = React.useState(null);
  const [motherFileUploader, setMotherFileUploader] = React.useState(null);
  const [childFileUploader, setChildFileUploader] = React.useState(null);

  const [numberOfAddedFiles, setNumberOfAddedFiles] = React.useState(0);
  const [FileUploadAlert, setFileUploadAlert] = React.useState(null);
  const [projectNameAlert, setProjectNameAlert] = React.useState(false);
  const [referenceGenome, setReferenceGenome] = React.useState("hg38");
  const [showReferenceToggle, setShowReferenceToggle] = React.useState(false);
  const [vcfReferenceWarning, setVcfReferenceWarning] = React.useState(null);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const projectType = searchParams.get("type");

  // Set predifined values for algorithms
  const [inputs, setInputs] = React.useState({
    project_type: projectType,
    aligner: [],
    variantCaller: [],
    variantAnnotator: [],
    reference_genome: "hg38",
  });

  const handleInput = (name, value) => {
    setInputs((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    setProjectNameAlert(false);
  };

  const handleReferenceGenomeChange = (newReference) => {
    setReferenceGenome(newReference);
    handleInput("reference_genome", newReference);

    // Clear VCF reference warning when user manually changes reference
    if (vcfReferenceWarning && vcfReferenceWarning.type === "hg19_detected") {
      setVcfReferenceWarning(null);
    }
  };

  // Reusable function to process and post sample data
  const processSample = async (uploader, sampleType, uploaderName = "") => {
    if (!uploader) return null;

    try {
      const files = await uploader.processFiles();

      const sampleData = {
        file_ids: files.map((file) => file.serverId),
        sample_type: sampleType,
      };

      // Only include reference_genome if it's not hg38
      if (referenceGenome !== "hg38") {
        sampleData.reference_genome = referenceGenome;
      }

      const sampleResponse = await postSample(sampleData);
      return sampleResponse.data;
    } catch (error) {
      console.error(`Error processing ${uploaderName} sample:`, error);
      throw error; // Re-throw to handle in calling context
    }
  };

  // Reusable function to process bed files
  const processBedFile = async (uploader) => {
    if (!uploader) return null;

    try {
      const bedFiles = await uploader.processFiles();
      return bedFiles.map((file) => file.serverId);
    } catch (error) {
      console.error("Error processing bed file:", error);
      return null;
    }
  };

  const handleCreateProject = async () => {
    if (!inputs.name) {
      setProjectNameAlert(true);
      return;
    }

    // Create form data
    const formData = new FormData();

    if (projectType === "GERMLINE_TRIO") {
      // Check if child sample is uploaded, which is required for trio analysis
      if (childFileUploader.getFiles().length < 1) {
        setFileUploadAlert({
          type: "missing_files",
          message:
            "Please upload or select a child sample for the trio analysis.",
          severity: "error",
        });
        return;
      }

      try {
        // Process trio samples in parallel where possible
        const [fatherSampleId, motherSampleId, childSampleId] =
          await Promise.allSettled([
            processSample(fatherFileUploader, "NORMAL", "father"),
            processSample(motherFileUploader, "NORMAL", "mother"),
            processSample(childFileUploader, "NORMAL", "child"),
          ]);

        // Handle father sample
        if (fatherSampleId.status === "fulfilled" && fatherSampleId.value) {
          formData.append("father_sample_id", fatherSampleId.value);
        } else if (fatherSampleId.status === "rejected") {
          console.error(
            "Father sample processing failed:",
            fatherSampleId.reason
          );
        }

        // Handle mother sample
        if (motherSampleId.status === "fulfilled" && motherSampleId.value) {
          formData.append("mother_sample_id", motherSampleId.value);
        } else if (motherSampleId.status === "rejected") {
          console.error(
            "Mother sample processing failed:",
            motherSampleId.reason
          );
        }

        // Collect all proband sample IDs
        const probandSampleIds = [];

        // Handle child sample (required - first proband)
        if (childSampleId.status === "fulfilled" && childSampleId.value) {
          probandSampleIds.push(childSampleId.value);
        } else {
          console.error(
            "Child sample processing failed:",
            childSampleId.reason
          );
          setFileUploadAlert({
            type: "processing_error",
            message: "Failed to process child sample. Please try again.",
            severity: "error",
          });
          return;
        }

        // Process additional probands if they exist
        const additionalProbandUploaders = probandRefs?.current || [];
        if (additionalProbandUploaders.length > 0) {
          for (let i = 0; i < additionalProbandUploaders.length; i++) {
            const uploader = additionalProbandUploaders[i];
            if (uploader && uploader.getFiles().length > 0) {
              try {
                const probandSampleId = await processSample(
                  uploader,
                  "NORMAL",
                  `proband ${i + 2}`
                );
                if (probandSampleId) {
                  probandSampleIds.push(probandSampleId);
                }
              } catch (error) {
                console.error(
                  `Error processing additional proband ${i + 2}:`,
                  error
                );
              }
            }
          }
        }

        // Append all proband IDs as a JSON array
        // Append proband IDs as an array (not JSON stringified)
        probandSampleIds.forEach((probandId) => {
          formData.append("proband_sample_ids", probandId);
        });
      } catch (error) {
        console.error("Error processing trio samples:", error);
        return;
      }

      // Process additional probands if they exist
      const additionalProbandUploaders = probandRefs?.current || [];
      if (additionalProbandUploaders.length > 0) {
        const additionalProbands = [];

        for (let i = 0; i < additionalProbandUploaders.length; i++) {
          const uploader = additionalProbandUploaders[i];
          if (uploader && uploader.getFiles().length > 0) {
            try {
              const probandSampleId = await processSample(
                uploader,
                "NORMAL",
                `proband ${i + 1}`
              );
              if (probandSampleId) {
                additionalProbands.push(probandSampleId);
              }
            } catch (error) {
              console.error(
                `Error processing additional proband ${i + 1}:`,
                error
              );
            }
          }
        }

        // Append all additional probands with indexed keys
        additionalProbands.forEach((probandId, index) => {
          formData.append(`proband${index + 2}_sample_id`, probandId);
        });
      }
    } else {
      // Regular project file logic
      if (numberOfAddedFiles < 1) {
        setFileUploadAlert({
          type: "missing_files",
          message: "Please upload or select at least one sample.",
          severity: "error",
        });
        return;
      }

      try {
        // Process tumor and normal samples in parallel
        const samplePromises = [];

        if (projectType !== "GM" && tumorFileUploader) {
          samplePromises.push(
            processSample(tumorFileUploader, "TUMOR", "tumor")
              .then((sampleId) => ({ type: "tumor", sampleId }))
              .catch((error) => ({ type: "tumor", error }))
          );
        }

        if (normalFileUploader) {
          samplePromises.push(
            processSample(normalFileUploader, "NORMAL", "normal")
              .then((sampleId) => ({ type: "normal", sampleId }))
              .catch((error) => ({ type: "normal", error }))
          );
        }

        const sampleResults = await Promise.all(samplePromises);

        // Process results
        for (const result of sampleResults) {
          if (result.error) {
            console.error(
              `Error processing ${result.type} sample:`,
              result.error
            );
            if (result.type === "normal") {
              // Normal samples are often required, so return on error
              return;
            }
          } else if (result.sampleId) {
            formData.append(`${result.type}_sample_id`, result.sampleId);
          }
        }
      } catch (error) {
        console.error("Error processing samples:", error);
        return;
      }
    }

    // Process bed files (needed for all project types)
    const bedFileIds = await processBedFile(bedFileUploader);
    if (bedFileIds) {
      formData.append("bed_file_id", bedFileIds);
    }

    // Add HPO terms to form data
    if (selectedHPOTerms.length > 0) {
      formData.append(
        "hpo_terms",
        JSON.stringify(
          selectedHPOTerms.map((term) => ({
            id: term.id,
          }))
        )
      );
    }

    // Process algorithms
    const algorithmDict = {};
    for (const key in inputs) {
      if (Object.prototype.hasOwnProperty.call(inputs, key)) {
        if (algorithmKeys.includes(key)) {
          algorithmDict[`${key}`] = inputs[key];
        } else {
          formData.append(key, inputs[key]);
        }
      }
    }
    formData.append("algorithms", JSON.stringify(algorithmDict));

    // Post project to backend and redirect to project page
    postProject(formData)
      .then((response) => {
        if (response.status == "201") {
          navigate("../");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleAddFile = (error, file) => {
    if (error) return;

    // Get the filename - handle both direct file objects and objects with probandId
    const filename = file.filename || file.file?.name || "";

    if (filename.endsWith(".vcf")) {
      setShowReferenceToggle(true);

      // Get the actual file object
      const fileObject = file.file || file;

      // Check if we have a valid file object with slice method
      if (!fileObject || typeof fileObject.slice !== "function") {
        console.warn("Invalid file object for VCF reference parsing");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        let headerContent = e.target.result;

        // Find the end of headers (where #CHROM line appears)
        const lines = headerContent.split("\n");
        const headerEndIndex = lines.findIndex((line) =>
          line.startsWith("#CHROM")
        );

        if (headerEndIndex !== -1) {
          // Include only header lines up to #CHROM
          headerContent = lines.slice(0, headerEndIndex + 1).join("\n");
        }

        const refInfo = parseVCFForReference(headerContent);
        if (!refInfo.hasHg38Reference && !refInfo.hasHg19Reference) {
          setFileUploadAlert({
            type: "vcf_reference",
            message:
              "Error: VCF file reference genome could not be determined. Please ensure your VCF file uses hg19 or hg38 reference genome.",
            severity: "error",
          });
          return;
        }

        // If hg19 is detected and current setting is hg38, switch and warn
        if (refInfo.hasHg19Reference && referenceGenome === "hg38") {
          setReferenceGenome("hg19");
          handleInput("reference_genome", "hg19");
          setVcfReferenceWarning({
            type: "hg19_detected",
            message:
              "VCF file appears to use hg19 reference. Reference genome has been automatically switched to hg19. Note that liftover to hg38 may be performed during analysis.",
            severity: "warning",
          });
        }

        // If hg38 is detected but reference is set to hg19, suggest switching
        if (refInfo.hasHg38Reference && referenceGenome === "hg19") {
          setVcfReferenceWarning({
            type: "hg38_suggested",
            message:
              "VCF file appears to use hg38 reference, but hg19 is currently selected. Consider switching to hg38 for optimal results.",
            severity: "info",
          });
        }
      };
      reader.readAsText(fileObject.slice(0, 200000)); // Read 200KB
    } else {
      // For non-VCF files, hide the reference toggle
      setShowReferenceToggle(false);
      setVcfReferenceWarning(null);
    }
    setFileUploadAlert(null);
    setNumberOfAddedFiles(numberOfAddedFiles + 1);
  };

  const handleRemoveFile = () => {
    setFileUploadAlert(null);
    setNumberOfAddedFiles(numberOfAddedFiles - 1);

    // Check if any VCF files remain
    const hasVcfFiles = [
      tumorFileUploader,
      normalFileUploader,
      childFileUploader,
      fatherFileUploader,
      motherFileUploader,
    ].some(
      (uploader) =>
        uploader &&
        uploader.getFiles().some((file) => file.filename.endsWith(".vcf"))
    );

    if (!hasVcfFiles) {
      setShowReferenceToggle(false);
      setVcfReferenceWarning(null);
      // Reset to default hg38
      setReferenceGenome("hg38");
      handleInput("reference_genome", "hg38");
    }
  };

  // Add this near the top of your component where other state variables are declared
  const probandRefs = React.useRef([]);

  // Add HPO terms state
  const [selectedHPOTerms, setSelectedHPOTerms] = React.useState([]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box>
        <Typography variant="h5" color="secondary">
          New Project
        </Typography>
      </Box>
      <Box sx={{ width: { sm: "100vw", md: "25vw" } }}>
        <TextField
          error={projectNameAlert}
          name="name"
          label="Project Name"
          variant="standard"
          required={true}
          onChange={(e) => {
            handleInput(e.target.name, e.target.value);
          }}
          sx={{
            "& label.Mui-focused": {
              color: "black",
            },
          }}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" color="secondary">
          {projectType === "GERMLINE_TRIO"
            ? "Family Samples"
            : "Upload Sample Files"}
        </Typography>

        {projectType !== "GM" && projectType !== "GERMLINE_TRIO" ? (
          <Alert severity="info">
            Somatic variant calling can be made with tumor samples alone.
            However, suppying normal samples increases sensitivity and
            specificity.
          </Alert>
        ) : null}

        {FileUploadAlert ? (
          <Alert severity={FileUploadAlert.severity || "error"} sx={{ mt: 3 }}>
            {FileUploadAlert.message}
          </Alert>
        ) : null}
      </Box>

      {projectType === "GERMLINE_TRIO" ? (
        <React.Suspense fallback={<div>Loading...</div>}>
          <TrioUploader
            onAddFile={handleAddFile}
            onRemoveFile={handleRemoveFile}
            fatherFileUploader={setFatherFileUploader}
            motherFileUploader={setMotherFileUploader}
            childFileUploader={setChildFileUploader}
            probandRefs={probandRefs}
          />
        </React.Suspense>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 3,
          }}
        >
          <React.Suspense fallback={<div>Loading...</div>}>
            <Box sx={{ width: { sm: "100%", md: "20vw" }, mt: 1, mb: 2 }}>
              <FileUploader
                refSetter={setNormalFileUploader}
                title="Normal Samples"
                allowMultiple={true}
                sampleType="NORMAL"
                onAddfile={handleAddFile}
                onRemoveFile={handleRemoveFile}
                maxFiles={2}
              />
            </Box>

            {(projectType === "SOMATIC" || projectType === "COMP") && (
              <Box sx={{ width: { sm: "100%", md: "20vw" }, mt: 1, mb: 2 }}>
                <FileUploader
                  refSetter={setTumorFileUploader}
                  title="Tumor Samples"
                  allowMultiple={true}
                  sampleType="TUMOR"
                  onAddfile={handleAddFile}
                  onRemoveFile={handleRemoveFile}
                  maxFiles={2}
                />
              </Box>
            )}

            {/* BED file uploader alongside other uploaders */}
            <Box sx={{ width: { sm: "100%", md: "20vw" }, mt: 1, mb: 2 }}>
              <FileUploader
                refSetter={setBedFileUploader}
                title="BED File"
                tooltip={
                  <Tooltip title="BED file should be the same genome build as samples.">
                    <WarningIcon sx={{ mr: 1 }} htmlColor="#BBB539" />
                  </Tooltip>
                }
                allowMultiple={false}
                onAddfile={handleAddFile}
                onRemoveFile={handleRemoveFile}
                maxFiles={1}
              />
            </Box>
          </React.Suspense>
        </Box>
      )}

      {/* Algorithm selection - same for all project types that need it */}
      {projectType === "COMP" ? (
        <>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" color="secondary">
              Select Workflow Algorithms
            </Typography>
            <Alert severity="info">
              If you want to continue with default workflow, you can skip this
              part.
            </Alert>
          </Box>
          <Box
            sx={{ display: "flex", flexDirection: { xs: "column", md: "row" } }}
          >
            <Box sx={{ width: { sm: "100%", md: "20vw" }, mt: 1, mb: 3 }}>
              <AlgorithmSelector
                title="Aligner"
                name="aligner"
                options={mappers}
                type="Aligner"
                onChange={handleInput}
              />
            </Box>
            <Box
              sx={{
                width: { sm: "100%", md: "20vw" },
                mt: 1,
                mb: 3,
                ml: { xs: 0, md: 3 },
              }}
            >
              <AlgorithmSelector
                title="Variant Caller"
                name="variantCaller"
                options={variantCallers}
                type="Variant Detector"
                onChange={handleInput}
              />
            </Box>
            <Box
              sx={{
                width: { sm: "100%", md: "20vw" },
                mt: 1,
                mb: 3,
                ml: { xs: 0, md: 3 },
              }}
            >
              <AlgorithmSelector
                title="Variant Annotator"
                name="variantAnnotator"
                options={variantAnnotators}
                type="Variant Annotator"
                onChange={handleInput}
              />
            </Box>
          </Box>
        </>
      ) : null}

      {/* Reference Genome Selector - Modern Button Group */}
      {showReferenceToggle && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography
            variant="body2"
            sx={{
              mb: 1,
              color: "#666",
              fontWeight: 500,
            }}
          >
            Reference Genome
          </Typography>
          <ButtonGroup
            variant="contained"
            size="medium"
            sx={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              overflow: "hidden",
              "& .MuiButton-root": {
                borderRadius: 0,
                border: "none",
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                py: 1,
                transition: "all 0.2s ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                },
              },
              "& .MuiButton-root:first-of-type": {
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              },
              "& .MuiButton-root:last-of-type": {
                borderTopRightRadius: "8px",
                borderBottomRightRadius: "8px",
              },
            }}
          >
            <Button
              onClick={() => handleReferenceGenomeChange("hg38")}
              sx={{
                backgroundColor:
                  referenceGenome === "hg38" ? "#428AAE" : "#f5f5f5",
                color: referenceGenome === "hg38" ? "#fff" : "#666",
                "&:hover": {
                  backgroundColor:
                    referenceGenome === "hg38" ? "#357a96" : "#e0e0e0",
                },
              }}
            >
              hg38 (GRCh38)
            </Button>
            <Button
              onClick={() => handleReferenceGenomeChange("hg19")}
              sx={{
                backgroundColor:
                  referenceGenome === "hg19" ? "#428AAE" : "#f5f5f5",
                color: referenceGenome === "hg19" ? "#fff" : "#666",
                "&:hover": {
                  backgroundColor:
                    referenceGenome === "hg19" ? "#357a96" : "#e0e0e0",
                },
              }}
            >
              hg19 (GRCh37)
            </Button>
          </ButtonGroup>
        </Box>
      )}

      {/* VCF Reference Warning */}
      {vcfReferenceWarning && (
        <Alert severity={vcfReferenceWarning.severity} sx={{ mt: 1, mb: 1 }}>
          {vcfReferenceWarning.message}
        </Alert>
      )}

      {/* Add HPO Selector - only show for non-somatic project types */}
      {projectType !== "SOMATIC" && (
        <Box sx={{ mt: 3, mb: 3 }}>
          <HPOSelector
            selectedTerms={selectedHPOTerms}
            onSelectionChange={setSelectedHPOTerms}
            maxSelections={15}
          />
        </Box>
      )}

      <Box width={"100px"}>
        <Button
          variant="contained"
          onClick={handleCreateProject}
          disabled={FileUploadAlert && FileUploadAlert.severity === "error"}
          sx={{
            backgroundColor:
              FileUploadAlert && FileUploadAlert.severity === "error"
                ? "#ccc"
                : "#428AAE",
            color: "#fff",
            "&:disabled": {
              backgroundColor: "#ccc",
              color: "#999",
            },
          }}
        >
          Create
        </Button>
      </Box>
    </Box>
  );
}

export default CreateProject;
